import {
  createEventSponsor,
  internalRegistrationUser,
  listUser,
  magicLinkUser,
  magiclinkUrlUser,
  setCognitoUserPasswordUser,
  ssoMetadataUser,
  ssoRelayUser,
  ssoUser,
  statusUser,
  getCognitoUserAttributesUser,
  ResourceTypeEnum,
  User,
  UserRegistrationRequest,
  RolesEnum
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { Auth } from 'aws-amplify';
import { set as setInIDB, getMany, clear as clearIDB } from 'idb-keyval';
import cloneDeep from 'lodash.clonedeep';

import router from '../../router';
import { safeSignout, trimLowercaseString } from '../../utils/utils';
import { loadState } from '../helper';

import analytics, { trackLoginOrLogoutEvent } from '@/analytics';
import { handlePostLoginRedirect } from '@/router/routerHelpers';
import { UserCognitoDetails } from '@/services/cognitoService';
import { initDriftBot, resetDriftBot } from '@/services/driftBotService';
import { initializeFeatureFlags } from '@/services/featureFlag';
import { GatewayClient } from '@/services/gatewayClient';
import { sessionService } from '@/services/sessionService';
import { UserRole } from '@/studio/settings/OrgMembersSettings/utils';
import { UserSelectedRoleItem } from '@/studio/settings/Permissions/types';
import { updatePasswordLastSet } from '@/utils/cognito';
import { setCookieValue } from '@/utils/cookies';
import { getEnv } from '@/utils/env';
import { getErrorMessage } from '@/utils/errorUtils';
import { getCookieValue, showErrorNotification } from '@/utils/utils.js';

export const socket = new GatewayClient(getEnv('VUE_APP_API_GATEWAY_URL'), logger);
const zendesk_enabled = getEnv('VUE_APP_ZENDESK_KEY') !== 'false';

const defaultTrayUserToken = {
  token: null,
  tokenCreatedAt: null
};

const getDefaultState = () => ({
  currentUser: null,
  loadingRegisteredUserMagicLink: false,
  userCognitoDetails: null as UserCognitoDetails,
  sponsorUser: null,
  oktaLoginState: 'notstarted',
  registeredUserMagicLinkError: null,
  registeredUserMagicLink: null,
  trayUserToken: defaultTrayUserToken,
  advancedOptionsEnabled: window.localStorage.getItem('studio_advanced_options') === 'true',
  showSetNewPasswordDialog: false,
  forceChangePassword: false,
  ssoLoginInProgress: false
});

const state = getDefaultState();
state.currentUser = loadState().currentUser || null;
state.sponsorUser = loadState().sponsorUser || null;

const getters = {
  getCurrentUserId: state => {
    return state?.currentUser?.id ?? null;
  },
  getCurrentUser: state => {
    return state.currentUser;
  },
  getSponsorUser: state => {
    return state.sponsorUser;
  },
  // getJwtAccessToken is required by content app for authentication. Do not remove
  getJwtAccessToken: state => {
    return state.userCognitoDetails?.signInUserSession?.accessToken?.jwtToken;
  },
  getCurrentUserCognitoDetails: state => {
    return state.userCognitoDetails;
  },
  getIsCurrentUserEmailVerified: state => {
    return state.userCognitoDetails?.attributes?.email_verified;
  },
  getUserPlatformRoleByResource:
    () =>
    ({ resourceType, user }: { resourceType: ResourceTypeEnum; user: User }): UserSelectedRoleItem => {
      if (!user?.requester_role) {
        return {
          id: 0,
          name: ''
        };
      }

      const organizationId = user.organization;
      const filteredRequesterRole = user?.requester_role?.find(
        item => item?.resource_type === resourceType && item.resource_id === organizationId
      );

      if (!filteredRequesterRole) {
        return {
          id: 0,
          name: ''
        };
      }

      return {
        id: filteredRequesterRole.role,
        name: filteredRequesterRole.role__name as RolesEnum
      };
    },
  isCurrentUserOrgAdmin: (state, getters, rootState, rootGetters): boolean => {
    const isPermissionsEnabled = rootGetters['orgPricingConfig/getIsPermissionsEnabled'];

    const userPlatformRole = getters.getUserPlatformRoleByResource({
      resourceType: 'organization',
      user: state.currentUser
    }) as UserSelectedRoleItem;

    if (isPermissionsEnabled) {
      return userPlatformRole?.name === 'Organization Admin';
    }

    if (state.currentUser?.role === UserRole.ORG_ADMIN) {
      return true;
    }
    // permissions are not enabled so marking flexi_admin as org_admin
    return state.currentUser?.role === UserRole.FLEXI_ADMIN && state.currentUser?.workspaces?.length === 0;
  },
  isCurrentUserWorkspaceAdmin: (state, getters, rootState, rootGetters): boolean => {
    const isPermissionsEnabled = rootGetters['orgPricingConfig/getIsPermissionsEnabled'];

    const userPlatformRole = getters.getUserPlatformRoleByResource({
      resourceType: 'organization',
      user: state.currentUser
    }) as UserSelectedRoleItem;

    if (isPermissionsEnabled) {
      return userPlatformRole?.name === 'Team Admin';
    }

    if (state.currentUser?.role === UserRole.WORKSPACE_ADMIN) {
      return true;
    }
    // permissions are not enabled so marking flexi_admin as workspace_admin
    return state.currentUser?.role === UserRole.FLEXI_ADMIN && state.currentUser?.workspaces?.length > 0;
  },
  canCurrentUserAccessAllWorkspaces: (state): boolean => {
    if (state.currentUser?.role === UserRole.ORG_ADMIN) {
      return true;
    }

    return state.currentUser?.role === UserRole.FLEXI_ADMIN && state.currentUser?.workspaces?.length === 0;
  }
};

const mutations = {
  setCurrentUser(state, user) {
    state.currentUser = user;
  },
  setCurrentUserRole(state, role) {
    state.currentUser.role = role;
  },
  setSponsorUser(state, user) {
    state.sponsorUser = user;
  },
  setRegisteredUserMagicLinkError(state, error) {
    state.registeredUserMagicLinkError = error;
  },
  setRegisteredUserMagicLinkLoading(state, loadingRegisteredUserMagicLink) {
    state.loadingRegisteredUserMagicLink = loadingRegisteredUserMagicLink;
  },
  setRegisteredUserMagicLink(state, magicLink) {
    state.registeredUserMagicLink = magicLink;
  },
  RESET_USER(state) {
    Object.assign(state, getDefaultState());
  },
  setCognitoUserDetails(state, details) {
    state.userCognitoDetails = details;
  },
  setTrayUserToken(state, userToken) {
    state.trayUserToken = userToken;
  },
  setCognitoEmailVerified(state, emailVerified) {
    if (!state.userCognitoDetails?.attributes) {
      return;
    }

    state.userCognitoDetails.attributes.email_verified = emailVerified;
  },
  toggleShowSetNewPasswordDialog(state, show: boolean) {
    state.showSetNewPasswordDialog = show;
  },
  setForceChangePassword(state, forceChangePassword: boolean) {
    state.forceChangePassword = forceChangePassword;
  },
  setSsoLoginInProgress(state, inProgress: boolean) {
    state.ssoLoginInProgress = inProgress;
  },
  setOktaLoginState(state, oktaLoginState: string) {
    state.oktaLoginState = oktaLoginState;
  }
};

const actions = {
  checkTokenAndLogin(context, payload) {
    context.commit('setSsoLoginInProgress', true);
    // Make api call with token
    return getCurrentUserFromCognito()
      .then(user => {
        if (!user) {
          // No user is logged in so continue
          return;
        }

        return safeSignout();
      })
      .then(() => {
        if (payload.code) {
          return ssoUser({ queryParams: { code: payload.code } });
        }

        if (payload.token) {
          return ssoRelayUser({
            body: {
              token: payload.token
            }
          });
        }

        throw new Error('Neither token nor code was provided for sso');
      })
      .then((res: any) => {
        const email = res.email,
          secretCode = res.token;
        return context.dispatch('loginWithSsoMagicToken', { email: email.toLowerCase(), secretCode });
      })
      .catch(err => {
        logger.error('Failed to login:', err);
        trackLoginOrLogoutEvent({ eventType: 'Login', isSuccess: false, loginMethod: 'sso' });
        if (err?.reason === 'invalid token') {
          context.commit('setOktaLoginState', 'notallowed');
        } else {
          context.commit('setOktaLoginState', 'failed');
        }
        context.commit('setSsoLoginInProgress', false);
      });
  },
  loginWithSsoMagicToken(context, payload) {
    return safeSignout().then(() => {
      return Auth.signIn(payload.email)
        .then(user => {
          return Auth.sendCustomChallengeAnswer(user, payload.secretCode, { loginMethod: 'sso' });
        })
        .then(cognitoUser => {
          if (!cognitoUser.signInUserSession) {
            context.commit('setOktaLoginState', 'failed');
            logger.error('Failed in login: ', cognitoUser);
            throw new Error('Invalid magic link');
          } else {
            setCookieValue('logged_in_via_sso', true);
            trackLoginOrLogoutEvent({ eventType: 'Login', isSuccess: true, loginMethod: 'sso' });
            context.commit('setSsoLoginInProgress', false);
            return context.dispatch('updateCurrentAppUser', false);
          }
        })
        .then(() => handlePostLoginRedirect(true));
    });
  },
  setTrayUserToken(context, userToken) {
    context.commit('setTrayUserToken', userToken);
  },
  async getNewAccessToken(context) {
    const cognitoUserDetails = await getCurrentUserFromCognito();
    if (!cognitoUserDetails) {
      context.commit('setCurrentUser', null);
      return;
    }
    context.commit('setCognitoUserDetails', cognitoUserDetails);
  },
  async updateCurrentAppUser(context) {
    if (context.state.ssoLoginInProgress) {
      return;
    }
    context.commit('org/setLoadingOrgData', true, { root: true });

    let cognitoUserDetails: any = null,
      userData: any = null,
      currentOrg: any = null,
      cognitoEmailVerified: any = null;
    try {
      const dbVals = await getMany(['cognitoUserDetails', 'userData', 'currentOrg', 'cognitoEmailVerified']);
      cognitoUserDetails = dbVals[0];
      userData = dbVals[1];
      currentOrg = dbVals[2];
      cognitoEmailVerified = dbVals[3];
      logger.info('IDB values: ', cognitoUserDetails, userData, currentOrg);
    } catch (err) {
      logger.error('Cannot perform indexDB operation: ', err);
    }

    try {
      if (cognitoUserDetails?.data && currentOrg?.data && userData?.data && cognitoEmailVerified?.data) {
        context.commit('setCognitoUserDetails', cognitoUserDetails.data);
        context.commit('setCognitoEmailVerified', cognitoEmailVerified?.data || false);
        context.commit('setCurrentUser', userData.data);
        context.commit('org/setCurrentOrg', currentOrg.data, { root: true });
      }
      await context.dispatch('updateCurrentAppUserAPIs');
    } catch (e) {
      logger.error(e);
      return;
    } finally {
      context.commit('org/setLoadingOrgData', false, { root: true });
    }
  },
  async updateCurrentAppUserAPIs(context) {
    return new Promise((resolve, reject) => {
      try {
        clearIDB();
        logger.info('Cleared idb');
      } catch (err) {
        logger.error('Failed to clear idb', err);
      }

      try {
        logger.info('Getting user from cognito');
        getCurrentUserFromCognito().then(cognitoUserDetails => {
          const userEmail = cognitoUserDetails?.attributes?.email;
          if (!cognitoUserDetails || !userEmail) {
            context.commit('setCurrentUser', null);
            if (zendesk_enabled && window.zE) {
              window.zE(function () {
                window.zE('webWidget', 'reset');
              });
            }
            resolve(void 0);
            return;
          }

          context.commit('setCognitoUserDetails', cognitoUserDetails);

          listUser({ queryParams: { email: userEmail } }).then(resUsers => {
            socket.connect();

            const user = resUsers[0];
            const userData = {
              id: user.id,
              identityId: null,
              is_admin: user.is_admin,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email ? user.email : cognitoUserDetails.username,
              company: user.company,
              profilePictureUrl: null,
              location: null,
              tags: [],
              owner: null,
              organization: user.organization,
              role: user.role,
              workspaces: user.workspaces,
              is_content_lab_standalone: user.is_content_lab_standalone,
              requester_role: user.requester_role
            };

            let userSSOPromise = Promise.resolve();

            if (userData.organization) {
              userSSOPromise = context.dispatch('getUserSSOMetadata', {
                email: userData.email
              });
            }

            userSSOPromise
              .then((userSSOMetadata: any) => {
                if (userSSOMetadata?.only_sso && !getCookieValue('logged_in_via_sso')) {
                  context.dispatch('signUserOut');

                  if (router.currentRoute.value.name !== 'Login') {
                    router.push({ name: 'Login' }).catch(e => {
                      logger.error('Error in redirecting to login page: ', e);
                    });
                  }

                  const toastMessage = {
                    msg: "Username and password access is disabled for this account. To access Goldcast, log in using your organization's SSO.",
                    mode: 'error'
                  };
                  context.dispatch('studioToast/triggerToast', toastMessage, {
                    root: true
                  });
                  resolve(void 0);
                  return;
                }

                logger.identify(userData.id, {
                  name: userData.first_name.concat(' ', userData.last_name),
                  email: userData.email,
                  organization: userData.organization
                });
                analytics.identify(user.id, {
                  first_name: user.first_name,
                  last_name: user.last_name,
                  company: user.company,
                  organization: user.organization,
                  email: userData.email
                });
                if (zendesk_enabled && window.zE) {
                  window.zE(function () {
                    window.zE.identify({
                      name: userData.first_name.concat(' ', userData.last_name),
                      email: userData.email
                    });
                  });
                }
                context.commit('setCurrentUser', userData);
                const organization_id = user.organization;
                if (!organization_id) {
                  if (
                    ![
                      'ContentLabAlreadyLoggedIn',
                      'CreateOrg',
                      'ContentLabSharedClipPage',
                      'ContentLabSharedPostPage'
                    ].includes(router.currentRoute.value.name as string)
                  ) {
                    router
                      .push({
                        name: 'ContentLabAlreadyLoggedIn'
                      })
                      .catch(err => {
                        logger.error('Error in redirecting to access page: ', err);
                      });
                  }
                  resolve(void 0);
                } else {
                  context.commit('org/setLoadingOrgData', true, { root: true });
                  try {
                    context.dispatch('org/retrieveOrg', organization_id, { root: true }).then(async organization => {
                      logger.info('Org downloaded: ', organization);
                      try {
                        setInIDB('cognitoUserDetails', {
                          data: cognitoUserDetails.signInUserSession
                        });
                        setInIDB('userData', { data: userData });
                        setInIDB('currentOrg', { data: organization });
                        setInIDB('cognitoEmailVerified', { data: cognitoUserDetails.attributes.email_verified });
                      } catch (err) {
                        logger.error('Failed to set data in idb:', err);
                      }
                      try {
                        await initializeFeatureFlags();
                      } catch (err: any) {
                        logger.error('Failed to initialize feature flags:', err);
                      }
                      context
                        .dispatch('orgPricingConfig/getOrgPricingConfig', organization_id, { root: true })
                        .then(orgPricingConfig => {
                          initDriftBot(orgPricingConfig, userData);
                        });
                      context.dispatch('tags/fetchTags', {}, { root: true });
                      context.dispatch('workspace/fetchAllWorkspaces', {}, { root: true });
                      if (
                        (router.currentRoute.value.name === 'Login' || router.currentRoute.value.name === 'OrgLogin') &&
                        userData?.id
                      ) {
                        router.push({
                          name: 'OrgHome'
                        });
                      }
                      // Start session tracking after successful authentication
                      sessionService.startSession();
                      resolve(void 0);
                    });
                  } catch (err) {
                    const errorMessage = {
                      msg: 'Failed to fetch organization!',
                      mode: 'error'
                    };
                    context.dispatch('studioToast/triggerToast', errorMessage, {
                      root: true
                    });
                    reject();
                  } finally {
                    context.commit('org/setLoadingOrgData', false, {
                      root: true
                    });
                  }
                }
              })
              .catch(() => {
                context.dispatch('signUserOut');
                if (router.currentRoute.value.name !== 'Login') {
                  router.push({ name: 'Login' }).catch(e => {
                    logger.error('Error in redirecting to login page: ', e);
                  });
                }
              });
          });
        });
      } catch (err) {
        reject();
      }
    });
  },
  async checkForceChangePassword(context) {
    const currentUser = context.state.currentUser;

    if (!currentUser) {
      return;
    }

    try {
      const res = await getCognitoUserAttributesUser({ id: currentUser.id });
      if (res?.UserStatus === 'FORCE_CHANGE_PASSWORD') {
        context.commit('setForceChangePassword', true);
      }
    } catch (err) {
      logger.error('Failed to check force change password: ', err);
    }
  },
  showForceChangePasswordDialogIfRequired(context) {
    if (context.state.forceChangePassword) {
      context.commit('toggleShowSetNewPasswordDialog', true);
    }
  },
  resetUser(context) {
    const { commit } = context;
    if (zendesk_enabled && window.zE) {
      window.zE(function () {
        window.zE('webWidget', 'chat:removeTags', context.rootGetters['org/getCurrentOrg']?.name);
        window.zE('webWidget', 'close');
        window.zE('webWidget', 'reset');
      });
    }
    commit('RESET_USER');
    commit('orgIntegration/RESET_ORG_INTEGRATION_STORE', null, {
      root: true
    });
  },
  async createUser(_, userData: UserRegistrationRequest) {
    const body: UserRegistrationRequest = {
      email: userData.email,
      first_name: userData ? userData.first_name : '',
      last_name: userData ? userData.last_name : '',
      profile_picture_url: '',
      linkedin_url: userData ? userData.linkedin_url : '',
      twitter_url: userData ? userData.twitter_url : '',
      title: userData ? userData.title : '',
      company: userData ? userData.company : '',
      event_id: userData ? userData.event_id : '',
      ...((userData.is_magic ?? null) !== null && {
        is_magic: userData.is_magic
      }),
      organization: userData ? userData.organization : ''
    };

    if (userData?.role) {
      body.role = userData.role;
      body.workspaces = userData.workspaces;
    }

    if (userData?.extra_data) {
      body.extra_data = userData.extra_data;
      body.workspaces = userData.workspaces;
    }

    return internalRegistrationUser({
      body
    });
  },
  signUserOut(context) {
    return safeSignout().finally(() => {
      context.dispatch('resetUser');
      context.commit('analytics/setUserGroupToken', '', { root: true });
      router
        .replace({
          name: 'Login'
        })
        .then(() => {
          context.commit('branding/setBrandSettings', {}, { root: true });
          context.commit('branding/setCurrentModuleInvalid', false, {
            root: true
          });
          context.commit('org/clearOrgStore', null, { root: true });
          context.commit('studioEventList/resetEventListState', null, {
            root: true
          });
          context.commit('eventHubPages/resetEventHubState', null, {
            root: true
          });
          context.commit('studioTemplateList/resetTemplateListState', null, {
            root: true
          });
        });
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'You have been successfully logged out.',
          mode: 'success'
        },
        { root: true }
      );

      // clear all indexDB data
      try {
        clearIDB();
      } catch (err) {
        logger.error('Failed to clear idb during signout: ', err);
      }

      resetDriftBot();
    });
  },
  async resendMagicLink(_, { email, organization }) {
    return magicLinkUser({
      body: {
        email,
        ...(organization && { organization })
      }
    });
  },
  async createSponsorUser({ commit }, userData) {
    const payload = {
      event: userData ? userData.eventId : '',
      user: {
        email: userData.email,
        first_name: userData ? userData.first_name : '',
        last_name: userData ? userData.last_name : '',
        company: userData ? userData.company : ''
      }
    };

    return new Promise((resolve, reject) => {
      createEventSponsor({ body: payload })
        .then(() => {
          commit('setSponsorUser', userData);
          resolve('success');
        })
        .catch(reject);
    });
  },
  async getMagicLinkUrl(context, email) {
    try {
      context.commit('setRegisteredUserMagicLinkLoading', true);
      context.commit('setRegisteredUserMagicLink', null);
      const eventId = router.currentRoute.value.params.eventId as string;

      const res = await magiclinkUrlUser({
        queryParams: {
          email,
          event_id: eventId
        }
      });

      context.commit('setRegisteredUserMagicLink', res?.url);
    } catch (e) {
      context.commit('setRegisteredUserMagicLinkError', e);
      showErrorNotification('Copy magic link failed!');
    } finally {
      context.commit('setRegisteredUserMagicLinkLoading', false);
    }
  },
  async getUserStatus(_, payload) {
    return statusUser({ body: payload });
  },
  async resendEmail(context, { userEmail, type }) {
    try {
      await Auth.forgotPassword(trimLowercaseString(userEmail));
      if (type === 'changePassword') {
        const toastMessage = {
          msg: `We have sent a secret code to ${userEmail}`,
          mode: 'success'
        };
        context.dispatch('studioToast/triggerToast', toastMessage, {
          root: true
        });
      }
      if (type === 'forgotPassword') {
        router.push({
          name: 'ForgotPasswordSubmit',
          query: { email: userEmail }
        });
      }
    } catch (err: any) {
      if (err?.code === 'InvalidParameterException') {
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: `We are unable to reset your password since your account isn't verified. Please reach out to <a target="_blank" href="mailto:support@goldcast.io">support@goldcast.io</a> to retrieve your account.`,
            mode: 'error',
            action: {
              label: 'Send Email',
              callback: () => window.open('mailto:support@goldcast.io')
            }
          },
          { root: true }
        );
        return;
      }

      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: err?.message || 'Something went wrong. Please try again.',
          mode: 'error'
        },
        { root: true }
      );
    }
  },
  async forgotPasswordAuth(context, { userEmail, code, newPassword }) {
    try {
      await Auth.forgotPasswordSubmit(userEmail, code, newPassword);
      const toastMessage = {
        msg: 'Your password has been successfully updated. Please login with your new password. Redirecting to login page...',
        mode: 'success'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
      // We are doing multiple signin and signout below because aws
      // does not provide us with a way to revoke all sessions
      // when the user changes his password.
      // The following is the recommended fix by aws as described here:
      // https://github.com/aws-amplify/amplify-js/issues/6559#issuecomment-1172787591
      await Auth.signIn(userEmail, newPassword);
      await safeSignout();
      router.replace({
        name: 'Login'
      });
    } catch (err: any) {
      let msg = err?.message || 'Something went wrong. Please try again.';
      if (err?.code === 'PasswordHistoryPolicyViolationException') {
        msg = 'Password cannot be one of your last 3 used. Please choose a new password.';
      }
      const toastMessage = {
        msg,
        mode: 'error'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
    }
  },
  async submitTempPassword(context, { userEmail, password }) {
    try {
      await safeSignout();
      const user = await Auth.signIn(userEmail, password);
      if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        context.commit('setCognitoUserDetails', user);
        router.replace({
          name: 'External'
        });
        return;
      }
    } catch (err: any) {
      const toastMessage = {
        msg: err?.message || 'Something went wrong. Please try again.',
        mode: 'error'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
    }
  },
  async completeNewPassword(context, { password }) {
    try {
      const userCognitoDetails = cloneDeep(context.state.userCognitoDetails);
      const updatedUser = await Auth.completeNewPassword(userCognitoDetails, password);
      await updatePasswordLastSet(updatedUser);
      context.commit('setForceChangePassword', false);
      await context.dispatch('updateCurrentAppUser', false);
      const user = context.state.currentUser;
      if (user?.organization) {
        router.push({
          name: 'OrgHome'
        });
      }
    } catch (err: any) {
      const toastMessage = {
        msg: err?.message || 'Something went wrong. Please try again.',
        mode: 'error'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
    }
  },
  async setPasswordViaBackend(context, { password }) {
    const currentUser = context.state.currentUser;
    if (!currentUser?.id) {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Something went wrong. Please try again.', mode: 'error' },
        { root: true }
      );
      return;
    }
    try {
      await setCognitoUserPasswordUser({ id: currentUser.id, body: { password } });
      const cognitoUser = await getCurrentUserFromCognito();
      if (cognitoUser) {
        await updatePasswordLastSet(cognitoUser);
      }
      context.commit('setForceChangePassword', false);
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Password set successfully.', mode: 'success' },
        { root: true }
      );
      router.push({ name: 'OrgHome' });
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, 'Something went wrong. Please try again.');
      context.dispatch('studioToast/triggerToast', { msg: errorMessage, mode: 'error' }, { root: true });
    }
  },
  async getUserSSOMetadata(_, { email }) {
    return ssoMetadataUser({ queryParams: { email } });
  }
};

function getCurrentUserFromCognito() {
  return Auth.currentAuthenticatedUser()
    .then(cognitoUserDetails => {
      return cognitoUserDetails;
    })
    .catch(() => {
      return null;
    });
}

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
